import { useState } from 'react';
import { KV } from 'types/utils';
import { useVisualViewport } from '@navi-toolbox/hooks';

interface UpscaleParams {
  x2: boolean;
  size: Size;
}

type Src = string | null | undefined;

type Size = { w?: string; h?: string };

export type Sizes = KV<Size>;
type Photos = {
  [key: string]: {
    x: string | null;
    xx: string | null;
  };
};

type Options = {
  c?: string;
  b?: string;
};

const upscaleQuality = (
  url: string,
  { x2 = false, size }: UpscaleParams,
  options?: Options,
  scaled = false,
) => {
  const drp = {
    scaled: {
      x: '2.0',
      xx: '4.0',
    },
    standart: {
      x: '1.0',
      xx: '2.0',
    },
  };

  const dprParameter = drp[scaled ? 'scaled' : 'standart'][x2 ? 'xx' : 'x'];
  const widthParameter = size.w ? `w_${size.w}` : '';
  const heightParameter = size.h ? `h_${size.h}` : '';

  return url.replace(
    /\/upload/,
    `/upload/${options ? `${buildOptions(options)}/` : ''}f_auto/q_auto/dpr_${[
      dprParameter,
      'c_pad',
      widthParameter,
      heightParameter,
    ]
      .filter(Boolean)
      .join('/')}`,
  );
};

export const getOptimizedImages = (src: Src, sizes: Sizes, options?: Options, scaled?: boolean) => {
  return Object.keys(sizes).reduce((acc, size) => {
    acc[size] = {
      // encodeURI is used for URLs with UTF-8 characters
      x: src ? upscaleQuality(encodeURI(src), { x2: false, size: sizes[size] }, options, scaled) : null,
      xx: src ? upscaleQuality(encodeURI(src), { x2: true, size: sizes[size] }, options, scaled) : null,
    };

    return acc;
  }, {} as Photos);
};

export const useOptimizedImages = (src: Src, sizes: Sizes, options?: Options) => {
  const [scaled, setScaled] = useState(false);

  useVisualViewport((viewport) => viewport.scale > 2 && setScaled(true), { handle: 'resize' });

  return getOptimizedImages(src, sizes, options, scaled);
};

export const buildOptions = (options: Options) => {
  const result = [];

  for (const [k, v] of Object.entries(options)) {
    result.push(`${k}_${v}`);
  }

  return result.join('/');
};
